import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import styles from './Home.module.css';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

import picketRailingImage from '../assets/images/picketrailing.webp';
import glassRailingImage from '../assets/images/glassrailing.webp';
import stairRailingImage from '../assets/images/stairrailing.webp';

const Home = () => {
  const navigate = useNavigate();

  const navigateToContact = () => {
    navigate('/contact');
  };

  useEffect(() => {
    const handleScroll = () => {
      const background = document.querySelector(`.${styles.heroBackground}`);
      const offset = window.scrollY;
      background.style.transform = `translateY(${offset * 0.5}px)`;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const products = [
    {
      name: 'Picket Railing',
      description: 'Classic and versatile, our picket railings offer timeless elegance and superior safety.',
      image: picketRailingImage
    },
    {
      name: 'Glass Railing',
      description: 'Modern and sleek, our glass railings provide unobstructed views while ensuring maximum safety.',
      image: glassRailingImage
    },
    {
      name: 'Stair Railing',
      description: 'Functional and stylish, our stair railings combine safety with a seamless design.',
      image: stairRailingImage
    }
  ];

  const advantages = [
    'Guard rail compliant with NBC-AE (2023)',
    'Robust welded panel and post system',
    'Stair angles match local precast concrete stairs',
    'Simple and accurate site assembly',
    'Supply only or installed',
    'Inventory ready for install',
    'Custom angled sleeves available for non-square deck shapes',
    'Custom colors available',
    'Post over rail and side mount options available'
  ];

  const stockedColors = [
    { name: 'Gloss White', code: '#FFFFFF' },
    { name: 'Textured Black', code: '#000000' },
  ];

  const customColors = [
    { name: 'Dark Grey', code: '#2a2b2b' },
    { name: 'Grey', code: '#5e5f61' },
    { name: 'Brown', code: '#8B4513' },
    { name: 'Cream', code: '#f5f4d0' },
    { name: 'Multicolor', code: 'radial-gradient(circle, #1a1a1a , #4a4a4a, #2c3e50 , #8B4513 , #8B8000 , #7D3C98 , #8B0000 , #00008B )' },
  ];

  return (
    <div>
      <Helmet>
        <title>Home - BGRS - Builder Grade Railing System</title>
        <meta name="description" content="Setting new standards in builder grade railing design and functionality." />
        <meta name="keywords" content="railings, builder grade, aluminum railing, guardrail" />
      </Helmet>

      <div className={styles.heroSection}>
        <div className={styles.heroBackground} aria-hidden="true"></div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroHeading}>
            Setting New Standards in Builder Grade Railing Design and Functionality
          </h1>
          <p className={styles.heroSubheading}>
            From residential balconies to commercial staircases, we create railings that blend modern aesthetics with the strength and durability you can trust.
          </p>
          <button
            className={styles.quoteButton}
            onClick={navigateToContact}
            aria-label="Get a free quote"
          >
            Get a Free Quote
          </button>
        </div>
      </div>

      <section className={styles.productsSection} aria-labelledby="products-heading">
        <h2 id="products-heading" className={styles.sectionHeading}>Our Products</h2>
        <div className={styles.productGrid}>
          {products.map((product, index) => (
            <div key={index} className={styles.productCard} role="listitem">
              <img
                src={product.image}
                alt={product.name}
                className={styles.productImage}
                aria-hidden="true"
                loading="lazy"
              />
              <h3>{product.name}</h3>
              <p>{product.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.advantageSection} aria-labelledby="advantage-heading">
        <h2 id="advantage-heading" className={styles.sectionHeading}>The BGRS Advantage</h2>
        <div className={styles.advantageGrid} role="list">
          {advantages.map((advantage, index) => (
            <div key={index} className={styles.advantageItem} role="listitem">
              <CheckCircle className={styles.checkIcon} aria-hidden="true" />
              <p>{advantage}</p>
            </div>
          ))}
        </div>
        <div className={styles.colorSection}>
          <h3>Stocked Colors</h3>
          <div className={styles.colorGrid}>
            {stockedColors.map((color, index) => (
              <div key={index} className={styles.colorItem} role="listitem">
                <div
                  className={styles.colorDot}
                  style={{ backgroundColor: color.code }}
                  aria-hidden="true"
                ></div>
                <span className={styles.colorName}>{color.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.colorSection}>
          <h3>Custom Colors</h3>
          <div className={styles.colorGrid}>
            {customColors.map((color, index) => (
              <div key={index} className={styles.colorItem} role="listitem">
                <div
                  className={styles.colorDot}
                  style={{
                    background: color.code,
                  }}
                  aria-hidden="true"
                ></div>
              </div>
            ))}
          </div>
          <p className={styles.colorNote}>
            Extra charges may apply for any customizations. Longer lead times may be required.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;